import createClient from "openapi-fetch"
import type { components, paths } from "@/assets/api/openapi"
import { cloneDeep } from "lodash-es"
import type { ExamType } from "@/types/exam"

export const useAppApi = () => {
  const config = useRuntimeConfig()
  const { GET, POST, DELETE } = createClient<paths>({
    baseUrl: config.public.apiBase,
    headers: { accept: "application/json" },
  })

  const sessionStore = useSessionStore()
  const apiCache = useState<Partial<Record<keyof paths, any>>>(
    "apiCache",
    () => ({})
  )

  const getMedicalCouncils = async () => {
    if (apiCache.value["/app-api/medical-council/filtered"]) {
      return apiCache.value["/app-api/medical-council/filtered"]
    }
    const response = await GET("/app-api/medical-council/filtered")
    const {
      data,
      error,
      response: { status },
    } = response

    if (response) {
      sessionStore.updateSession()
    }

    const returnedValue = cloneDeep({ data, status, error })
    apiCache.value["/app-api/medical-council/filtered"] = returnedValue

    return returnedValue
  }

  const getBlockedApplicant = async (id: string) => {
    const { response, data, error } = await GET(
      "/app-api/blocked-applicant/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateBlockedApplicant = async (
    blockedApplicant: components["schemas"]["BlockedApplicant"]
  ) => {
    const { response, data, error } = await POST("/app-api/blocked-applicant", {
      body: blockedApplicant,
    })
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const checkBlockedApplicant = async (operationId: string) => {
    const { response, data, error } = await GET(
      "/app-api/blocked-applicant/check/{operationId}",
      {
        params: { path: { operationId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return {
      data,
      response,
      status: response?.status,
      error,
    }
  }

  const getOperationAdditionalDataAhByOperationId = async (id: string) => {
    const { response, data, error } = await GET(
      "/app-api/operation-additional-data-ah/by-operation/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateOperationAdditionalDataAh = async (
    operationAdditionalDataAh: components["schemas"]["OperationAdditionalDataAh"]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/operation-additional-data-ah",
      {
        body: operationAdditionalDataAh,
      }
    )

    if (response) {
      sessionStore.updateSession()
    }

    return { data, status: response.status, error }
  }

  const getOperationAdditionalDataPugByOperationId = async (id: string) => {
    const { response, data, error } = await GET(
      "/app-api/operation-additional-data-pug/by-operation/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateOperationAdditionalDataPug = async (
    operationAdditionalDataPug: components["schemas"]["OperationAdditionalDataPug"]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/operation-additional-data-pug/update",
      {
        body: operationAdditionalDataPug,
      }
    )

    if (response) {
      sessionStore.updateSession()
    }

    return { data, status: response.status, error }
  }

  const getReferenceProfession = async (id: number) => {
    const { response, data, error } = await GET(
      "/app-api/pug-reference-profession/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateReferenceProfession = async (
    referenceProfession: components["schemas"]["ProfessionalGroup"]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/pug-reference-profession",
      {
        body: referenceProfession,
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const deleteReferenceProfession = async (id: number) => {
    const { response, data, error } = await DELETE(
      "/app-api/pug-reference-profession/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateReferenceProfessionTextBlock = async (
    referenceProfessionId: number,
    referenceProfessionTextBlock: components["schemas"]["ProfessionalGroupsText"]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/pug-reference-profession/{id}/text-block",
      {
        body: referenceProfessionTextBlock,
        params: { path: { id: referenceProfessionId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const deleteReferenceProfessionTextBlock = async (
    referenceProfessionId: number,
    idTextBlock: string
  ) => {
    const { response, data, error } = await DELETE(
      "/app-api/pug-reference-profession/{id}/text-block/{idTextBlock}",
      {
        params: { path: { id: referenceProfessionId, idTextBlock } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateReferenceProfessionSubject = async (
    referenceProfessionId: number,
    subject: components["schemas"]["EducationSubject"]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/pug-reference-profession/{id}/subject",
      {
        body: subject,
        params: { path: { id: referenceProfessionId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const updateReferenceProfessionSubjectSorting = async (
    referenceProfessionId: number,
    subjectSorting: number[]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/pug-reference-profession/{id}/subject-sorting",
      {
        body: subjectSorting,
        params: { path: { id: referenceProfessionId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const deleteReferenceProfessionSubject = async (
    referenceProfessionId: number,
    subjectId: number
  ) => {
    const { response, data, error } = await DELETE(
      "/app-api/pug-reference-profession/{id}/subject/{subjectId}",
      {
        params: { path: { id: referenceProfessionId, subjectId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getReferenceProfessionEducationSubjectGroups = async () => {
    const { response, data, error } = await GET(
      "/app-api/pug-reference-profession/subject-groups"
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getReferenceProfessions = async () => {
    const { response, data, error } = await GET(
      "/app-api/pug-reference-profession"
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getUniversityByCountryId = async (countryId: number) => {
    const { response, data, error } = await GET(
      "/app-api/university/{countryId}",
      {
        params: { path: { countryId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getLanguageExaminationDataAh = async (stepId: string) => {
    const { response, data, error } = await GET(
      "/app-api/step-data-ah/language-examination/{stepId}",
      {
        params: { path: { stepId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getExamZip = async (examType: ExamType) => {
    const { response, data, error } = await GET(
      "/app-api/file/exam-zip/{examType}",
      {
        params: { path: { examType } },
        parseAs: "blob",
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    const contentDisposition = response.headers.get("content-disposition")
    let filename = "file.zip"

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1]
      }
    }
    return { data, filename, status: response.status, error }
  }

  const getWorkPermitDataAh = async (stepId: string) => {
    const { response, data, error } = await GET(
      "/app-api/step-data-ah/work-permit/{stepId}",
      {
        params: { path: { stepId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getKnowledgeExaminationDataAh = async (stepId: string) => {
    const { response, data, error } = await GET(
      "/app-api/step-data-ah/knowledge-examination/{stepId}",
      {
        params: { path: { stepId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getForeignTraining = async (id: number) => {
    const { response, data, error } = await GET(
      "/app-api/pug-reference-profession/foreign-training/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const deleteForeignTraining = async (id: number) => {
    const { response, error } = await DELETE(
      "/app-api/pug-reference-profession/foreign-training/{id}",
      {
        params: { path: { id } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { status: response.status, error }
  }

  const createOrUpdateForeignTraining = async (
    foreignTraining: components["schemas"]["ForeignTraining"]
  ) => {
    const { response, data, error } = await POST(
      "/app-api/pug-reference-profession/foreign-training",
      {
        body: foreignTraining,
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getSubjectByProfession = async (professionId: number) => {
    const { response, data, error } = await GET(
      "/app-api/pug-reference-profession/subject/{professionId}",
      {
        params: { path: { professionId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getForeignTrainingTemplates = async (stepId: string) => {
    const { response, data, error } = await GET(
      "/app-api/step-data-pug/foreign-training-templates/{stepId}",
      {
        params: { path: { stepId } },
      }
    )
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getDocumentNumberForAHOperation = async (operationId: string) => {
    const { response, data, error } = await GET(
      "/app-api/operation-additional-data-ah/document-number-for-operation/{id}",
      {
        params: { path: { id: operationId } },
      }
    )

    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const setDocumentNumberForAHOperation = async (
    idOperation: string,
    documentNumber: string
  ) => {
    const { response, data, error } = await POST(
      "/app-api/operation-additional-data-ah/document-number-for-operation",
      {
        body: { idOperation, documentNumber },
      }
    )

    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const addLegalChallengeWorkStep = async (idOperation: string) => {
    const { response, data, error } = await POST(
      "/app-api/legal-challenge/add-step/{idOperation}",
      {
        params: { path: { idOperation } },
      }
    )

    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const getBqfgData = async (type: "ah" | "pug", idOperation: string) => {
    const { response, data, error } = await GET(`/app-api/bqfg/{type}/{id}`, {
      params: { path: { type: type, id: idOperation } },
    })
    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const setBqfgData = async (
    type: "ah" | "pug",
    bqfgData: components["schemas"]["Bqfg"]
  ) => {
    const { response, data, error } = await POST(`/app-api/bqfg/{type}`, {
      body: bqfgData,
      params: { path: { type: type } },
    })

    if (response) {
      sessionStore.updateSession()
    }
    return { data, status: response.status, error }
  }

  const exportBqfg = async (
    type: "ah" | "pug",
    exportType: "INTERN" | "EXTERN",
    year: "CURRENT" | "PREVIOUS"
  ) => {

    const { response, data, error } = await GET(
        `/app-api/bqfg/{type}/export/{exportType}/{year}`,
        {
          params: { path: { type: type , exportType: exportType, year: year} },
          parseAs: "blob",
        }
    )
    if (response) {
      sessionStore.updateSession()
    }
    const contentDisposition = response.headers.get("content-disposition")
    let filename = "file.xlsx"

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/)
      if (filenameMatch && filenameMatch.length > 1) {
        filename = filenameMatch[1]
      }
    }
    return { data, filename, status: response.status, error }
  }

  return {
    getMedicalCouncils,
    getBlockedApplicant,
    updateBlockedApplicant,
    checkBlockedApplicant,
    getOperationAdditionalDataAhByOperationId,
    getOperationAdditionalDataPugByOperationId,
    updateOperationAdditionalDataAh,
    updateOperationAdditionalDataPug,
    getReferenceProfession,
    updateReferenceProfession,
    deleteReferenceProfession,
    updateReferenceProfessionTextBlock,
    deleteReferenceProfessionTextBlock,
    deleteReferenceProfessionSubject,
    updateReferenceProfessionSubjectSorting,
    updateReferenceProfessionSubject,
    getReferenceProfessionEducationSubjectGroups,
    getUniversityByCountryId,
    getLanguageExaminationDataAh,
    getWorkPermitDataAh,
    getKnowledgeExaminationDataAh,
    getExamZip,
    createOrUpdateForeignTraining,
    getForeignTraining,
    deleteForeignTraining,
    getSubjectByProfession,
    getForeignTrainingTemplates,
    getDocumentNumberForAHOperation,
    setDocumentNumberForAHOperation,
    getReferenceProfessions,
    getBqfgData,
    setBqfgData,
    exportBqfg,
    addLegalChallengeWorkStep,
  }
}
